/**
 * Owner: Masa Bayu
 * File: spsu-hyphenate-ms.ts
 * Date:Time: 2026-10-08 10:09
 * Description: OCR join · Mode B fail-closed gate on EXECUTE path.
 */

import { USE_FAIL_CLOSED_SLICE_ALPHA } from '@qx/qx-universal-engine/feature-flags';
import { repairSoftBreaks } from '../extract/spsu-repair-text';

/** Soft hyphen · valid break opportunity for CSS justify (U+00AD). */
export const SOFT_HYPHEN = '\u00AD';

export { repairSoftBreaks } from '../extract/spsu-repair-text';

const VOWELS = 'aeiouáéíóúàèìòùäëïöü';
const CONSONANTS = 'bcdfghjklmnpqrstvwxyz';
/** Words shorter than this stay unbroken. */
export const MIN_HYPHEN_WORD_LEN = 8;
/** Never leave fewer than this many letters on either side of a soft break. */
export const MIN_FRAGMENT_LEN = 3;

const WORD_RE = new RegExp(
  `[A-Za-z${VOWELS}${VOWELS.toUpperCase()}]+(?:['’][A-Za-z${VOWELS}${VOWELS.toUpperCase()}]+)?`,
  'g',
);

/**
 * OCR often leaves a space before a compound hyphen ("sumber -sumber").
 * Collapse to a true compound form before soft-hyphenation.
 */
export function repairCompoundHyphenSpacing(text: string): string {
  return text
    .replace(/(\p{L})\s+-\s*(\p{L})/gu, '$1-$2')
    .replace(/(\p{L})\s+-(?=\s|$)/gu, '$1-');
}

/**
 * Real words that must stay separated (do not glue across a space).
 * From Owner screenshot 2026-10-04 · te rsebut / bah awa / t erus are NOT in this set.
 */
const OCR_KEEP_SEPARATE = new Set([
  'di',
  'ke',
  'se',
  'dan',
  'ada',
  'ini',
  'itu',
  'tak',
  'nak',
  'dah',
  'pun',
  'lah',
  'kah',
  'toh',
  'wah',
  'kan',
  'buat',
  'satu',
  'tiga',
  'dua',
]);

/** Right-side tokens that must stay words (not glued after a consonant-final host). */
const OCR_KEEP_SEPARATE_RIGHT = new Set([
  ...OCR_KEEP_SEPARATE,
  'atau',
  'oleh',
  'untuk',
  'atas',
  'akan',
  'apa',
  'adalah',
  'agar',
  'orang',
  'yang',
  'dari',
  'pada',
  'dengan',
  'sebagai',
  'dalam',
  'sudah',
  'masih',
  'juga',
  'hanya',
  'iaitu',
  'apabila',
  'ia',
  'itu',
  'ini',
  'eh',
  'ah',
  'oh',
]);

/**
 * Illicit OCR join algorithm (DEBT-01) · retained for evidence / unit proof.
 * Live EXECUTE must use repairOcrWordSplitSpaces (Mode B gated).
 */
export function repairOcrWordSplitSpacesIllicit(text: string): string {
  let out = repairCompoundHyphenSpacing(text);
  // Tight punctuation · Eyes often inserts spaces inside "( H /96".
  out = out.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
  out = out.replace(/(\d)\s+\/\s*/g, '$1/').replace(/\s+\/\s*(?=\d)/g, '/');
  // Enclitics wrongly separated from host word.
  out = out.replace(/(\p{L})\s+(nya|lah|kah|pun|tah)\b/gu, '$1$2');
  // "pemerinta hannya" → pemerintahannya (enclitic absorbed into stub).
  out = out.replace(/(\p{L})\s+hannya\b/gu, '$1hannya');
  // Short OCR stubs only when the whole left token is 1–3 letters (not a word suffix).
  // (?<=^|\s) prevents "Keseluruhan perjalanan" → glue "an"+"perjalanan".
  let prev = '';
  while (out !== prev) {
    prev = out;
    out = out.replace(/(?<=^|\s)(\p{L}{1,3})\s+([a-zà-ü]{2,})/gu, (full, left: string, right: string) => {
      if (OCR_KEEP_SEPARATE.has(left.toLowerCase())) return full;
      return `${left}${right}`;
    });
    // Trailing syllable stubs · "Keruntuh an" → Keruntuhan (plate-fit river).
    out = out.replace(/(\p{L}{4,})\s+([a-zà-ü]{1,3})(?=\s|[.,;:!?)\]…]|$)/gu, (full, left: string, right: string) => {
      if (OCR_KEEP_SEPARATE.has(right.toLowerCase())) return full;
      if (OCR_KEEP_SEPARATE_RIGHT.has(right.toLowerCase())) return full;
      return `${left}${right}`;
    });
    // Mid-word · host ends consonant · next lowercase vowel-start ("memel ihara").
    out = out.replace(
      /(\p{L}*[bcdfghjklmnpqrstvwxyz])\s+([aeiouà-ü][a-zà-ü]{1,5})\b/gu,
      (full, left: string, right: string) => {
        if (OCR_KEEP_SEPARATE_RIGHT.has(right.toLowerCase())) return full;
        return `${left}${right}`;
      },
    );
  }
  return out;
}

/**
 * Live OCR join entry · Mode B α: NOT_EXECUTED (identity) when fail-closed ON.
 * FA-OCR-001 · tip text preserved · ≠ semantic repair invent.
 */
export function repairOcrWordSplitSpaces(text: string): string {
  if (USE_FAIL_CLOSED_SLICE_ALPHA) return text;
  return repairOcrWordSplitSpacesIllicit(text);
}

/**
 * Keep only soft breaks where every kept fragment is ≥ MIN_FRAGMENT_LEN letters.
 * Removes "te·rsebut" / "meng·ha·…" 1–2 letter stubs.
 */
export function pruneShortSoftHyphenFragments(hyphenated: string): string {
  const parts = hyphenated.split(SOFT_HYPHEN).filter((p) => p.length > 0);
  if (parts.length <= 1) return parts[0] ?? '';

  const kept: string[] = [parts[0]!];
  for (let i = 1; i < parts.length; i += 1) {
    const piece = parts[i]!;
    const leftLen = kept.join('').length;
    const rightLen = parts.slice(i).join('').length;
    if (
      leftLen >= MIN_FRAGMENT_LEN &&
      rightLen >= MIN_FRAGMENT_LEN &&
      piece.length >= MIN_FRAGMENT_LEN
    ) {
      kept.push(piece);
    } else {
      kept[kept.length - 1] = `${kept[kept.length - 1]!}${piece}`;
    }
  }
  return kept.join(SOFT_HYPHEN);
}

/**
 * Insert soft hyphens between CV|CV syllable pairs (v1.1 · deterministic · lightweight).
 * Example: "mengharapkan" → "mengha·rapkan" (no 1–2 letter stubs).
 */
export function insertSoftHyphensInWord(word: string): string {
  const plain = word.split(SOFT_HYPHEN).join('');
  if (plain.length < MIN_HYPHEN_WORD_LEN) return plain;
  if (/^\d+$/.test(plain)) return plain;

  const lower = plain.toLowerCase();
  const digraphSafe = lower
    .replace(/ng/g, '\uE000')
    .replace(/ny/g, '\uE001')
    .replace(/sy/g, '\uE002')
    .replace(/kh/g, '\uE003');

  const cvCv = new RegExp(
    `([${CONSONANTS}\\uE000-\\uE003])([${VOWELS}])([${CONSONANTS}\\uE000-\\uE003])([${VOWELS}])`,
    'gi',
  );

  let out = digraphSafe;
  let prev = '';
  while (out !== prev) {
    prev = out;
    out = out.replace(cvCv, `$1$2${SOFT_HYPHEN}$3$4`);
  }

  out = out
    .replace(/\uE000/g, 'ng')
    .replace(/\uE001/g, 'ny')
    .replace(/\uE002/g, 'sy')
    .replace(/\uE003/g, 'kh');

  out = pruneShortSoftHyphenFragments(out);

  if (out.split(SOFT_HYPHEN).join('').toLowerCase() !== lower) return plain;
  return applyCaseMask(plain, out);
}

function applyCaseMask(original: string, hyphenatedLower: string): string {
  let oi = 0;
  let result = '';
  for (const ch of hyphenatedLower) {
    if (ch === SOFT_HYPHEN) {
      result += SOFT_HYPHEN;
      continue;
    }
    const src = original[oi] ?? ch;
    oi += 1;
    result += src === src.toUpperCase() && src !== src.toLowerCase() ? ch.toUpperCase() : ch;
  }
  return result;
}

/**
 * Soft-hyphenate long words in a paragraph string.
 * Idempotent · strips prior soft hyphens then reapplies v1.1 rules.
 */
export function insertSoftHyphens(text: string): string {
  if (!text) return text;
  const cleaned = text.split(SOFT_HYPHEN).join('');
  const repaired = repairOcrWordSplitSpaces(cleaned);
  return repaired.replace(WORD_RE, (word) => insertSoftHyphensInWord(word));
}

/** Repair soft breaks + OCR join artifacts on any body paragraph (justify or not). */
export function repairBodyParagraphText(text: string): string {
  const withoutSoftHyphen = text.split(SOFT_HYPHEN).join('');
  return repairOcrWordSplitSpaces(repairSoftBreaks(withoutSoftHyphen));
}

/** Body justify only · leave title/running/caption untouched. */
export function softHyphenateJustifiedBodyContent(input: {
  readonly content: string;
  readonly textAlign: string;
  readonly role?: string;
}): string {
  const repaired = repairBodyParagraphText(input.content);
  if (input.textAlign !== 'justify') return repaired;
  if (input.role != null && input.role !== 'body') return repaired;
  return insertSoftHyphens(repaired);
}
